import React from 'react';
import styled from 'styled-components';
import { timeValueToDate, sort } from '../../lib/commonFunc/commonFunc';
import { MdAdd, MdDateRange, MdFitnessCenter, MdDelete } from 'react-icons/md';
import Loading from '../common/Loading';

const Container = styled.div`
  padding: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 20px;
  min-height: 260px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #ccc;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  color: #333;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background-color: #1565c0;
  }

  svg {
    font-size: 20px;
  }
`;

const PrescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PrescriptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #e9ecef;
    transform: translateX(4px);
  }
`;

const PrescriptionInfo = styled.div`
  flex: 1;
`;

const PrescriptionName = styled.div`
  color: #333;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PrescriptionBadge = styled.span`
  background-color: #1976d2;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
`;

const PrescriptionDate = styled.div`
  color: #888;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeleteButton = styled.button`
  padding: 6px;
  cursor: pointer;
  background: transparent;
  border: none;
  color: #999;
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #d32f2f;
    background-color: #ffebee;
  }

  svg {
    font-size: 18px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
  font-size: 15px;
  padding: 40px 20px;
`;

const LoadingWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Prescription = ({
  prescriptions,
  onOpenModal,
  loading,
  onClick,
  onClickPrescription,
}) => {
  if (loading) {
    return (
      <Container>
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      </Container>
    );
  }

  if (!prescriptions) {
    return (
      <Container>
        <ContentWrapper>
          <LeftSection>
            <Header>
              <Title>
                <MdFitnessCenter />
                운동 처방
              </Title>
            </Header>
            <ContentArea>
              <EmptyState style={{ padding: '20px 0', textAlign: 'left', height: 'auto' }}>
                <p>회원을 선택해 주세요</p>
              </EmptyState>
            </ContentArea>
          </LeftSection>
        </ContentWrapper>
      </Container>
    );
  }

  const sortedPrescriptions = prescriptions.length > 0 ? sort(prescriptions, 'registTime') : [];
  const hasRecords = sortedPrescriptions.length > 0;

  return (
    <Container>
      <ContentWrapper>
        <LeftSection>
          <Header>
            <Title>
              <MdFitnessCenter />
              운동 처방
            </Title>
            <AddButton onClick={onClick}>
              <MdAdd />
              처방하기
            </AddButton>
          </Header>
          <ContentArea>
            {hasRecords ? (
              <PrescriptionList>
                {sortedPrescriptions.map((item, index) => (
                  <PrescriptionItem key={item.fcProgramID}>
                    <PrescriptionInfo onClick={() => onClickPrescription(item)}>
                      <PrescriptionName>
                        {item.name}
                        <PrescriptionBadge>처방</PrescriptionBadge>
                      </PrescriptionName>
                      <PrescriptionDate>
                        <MdDateRange size={14} />
                        {timeValueToDate(item.registTime)}
                      </PrescriptionDate>
                    </PrescriptionInfo>
                    <DeleteButton onClick={(e) => {
                      e.stopPropagation();
                      onOpenModal(item);
                    }}>
                      <MdDelete />
                    </DeleteButton>
                  </PrescriptionItem>
                ))}
              </PrescriptionList>
            ) : (
              <EmptyState>불러올 처방이 없습니다.</EmptyState>
            )}
          </ContentArea>
        </LeftSection>
      </ContentWrapper>
    </Container>
  );
};

export default Prescription;
